import React, { useEffect, useState, useMemo, useRef } from "react";  // ✅ add useRef
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Autocomplete,
  Typography,Box            // ✅ add Typography for label
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import EmployeeTaxSlabVeiwController from "../../redux/actions/EmployeeTaxSlabApiAction";
import TaxApiViewController from "../../redux/actions/TaxSlabApiAction";
import ReactSignatureCanvas from "react-signature-canvas";           // ✅ signature pad
import { IMAGE_URL } from "../../services/api";

function EmpTaxSlabModal({ show, handleClose, initialData = null }) {
  const dispatch = useDispatch();
  const { addEmployeeTax, updateEmployeeTax } = EmployeeTaxSlabVeiwController();
  const { getTaxSlab } = TaxApiViewController();
  const { tax_slab, loading } = useSelector((state) => state.taxslab);

  const isEdit = Boolean(initialData?.id);
  const [selectedRegime, setSelectedRegime] = useState("old");

  // ✅ reference for signature pad
  const sigPadRef = useRef(null);

  const [initialValues, setInitialValues] = useState({
    financial_year: "",
    regime: "old",
    is_accepted: false,
  });

  const regimeOptions = [
    { label: "Old Regime (2024-2025)", value: "old" },
    { label: "New Regime (2025-2026)", value: "new" },
  ];

  const loadSignatureFromUrl = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous"; // needed if image is from another origin
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png")); // base64 string
    };
    img.onerror = reject;
    img.src = url;
  });
};



  useEffect(() => {
    if (show) {
      dispatch(getTaxSlab());

      if (initialData) {
        setInitialValues({
          financial_year: initialData.financial_year || "",
          regime: initialData.regime || "old",
          is_accepted: Boolean(initialData.is_accepted),
        });
        setSelectedRegime(initialData.regime || "old");

        // ✅ Load existing signature into pad when editing
       if (initialData?.sign && sigPadRef.current) {
    // If it is already base64, use directly
    if (initialData.sign.startsWith("data:image")) {
      sigPadRef.current.fromDataURL(initialData.sign);
    } else {
      // Otherwise, convert URL to base64 first
      loadSignatureFromUrl(initialData.sign)
        .then((dataURL) => {
          sigPadRef.current.fromDataURL(dataURL);
        })
        .catch((err) => console.error("Error loading signature:", err));
    }
  }
      } else {
        setInitialValues({
          financial_year: "",
          regime: "old",
          is_accepted: false,
        });
        setSelectedRegime("old");
      }
    } else {
      // clear when modal closes
      if (sigPadRef.current) sigPadRef.current.clear();
    }
  }, [show, initialData, dispatch]);

  // Filter tax slabs
  const filteredSlabs = useMemo(() => {
    return tax_slab
      .filter((slab) => slab.regime === selectedRegime)
      .sort((a, b) => parseFloat(a.min_income) - parseFloat(b.min_income));
  }, [tax_slab, selectedRegime]);

  const validationSchema = Yup.object({
    financial_year: Yup.string()
      .matches(/^\d{4}-\d{4}$/, "Format: YYYY-YYYY")
      .required("Financial year is required"),
    regime: Yup.string().required("Regime is required"),
  });

  // ✅ helper: convert base64 to File
  const dataURLtoFile = (dataurl, filename) => {
  const arr = dataurl.split(",");
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  return new File([u8arr], filename, { type: mime });
};

const handleSubmit = (values, { resetForm }) => {
  const formData = new FormData();
  formData.append("financial_year", values.financial_year);
  formData.append("is_accepted", values.is_accepted);
  formData.append(
    "tax_slab",
    JSON.stringify({
      regime: selectedRegime,
      slabs: filteredSlabs,
    })
  );

  // Only append signature if it exists
  if (sigPadRef.current && !sigPadRef.current.isEmpty()) {
  const dataURL = sigPadRef.current.toDataURL("image/png"); // base64
  const signatureFile = dataURLtoFile(dataURL, "signature.png");
  formData.append("sign", signatureFile); // ✅ must be a File object
}
  const action = isEdit
    ? updateEmployeeTax(initialData.id, formData, true) // true -> multipart
    : addEmployeeTax(formData, true);

  dispatch(action)
    .then(() => {
      resetForm();
      handleClose();
    })
    .catch((err) => console.error("Error submitting tax slab:", err));
};

  return (
    <Dialog open={show} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        {isEdit ? "Edit Employee Tax Slab" : "Add Employee Tax Slab"}
      </DialogTitle>

      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, setFieldValue }) => (
          <Form>
            <DialogContent dividers>
              <Grid container spacing={2}>
                {/* Regime */}
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={regimeOptions}
                    getOptionLabel={(option) => option.label}
                    value={regimeOptions.find((opt) => opt.value === selectedRegime)}
                    onChange={(event, newValue) => {
                      if (newValue) {
                        setSelectedRegime(newValue.value);
                        setFieldValue("regime", newValue.value);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Regime"
                        error={touched.regime && Boolean(errors.regime)}
                        helperText={touched.regime && errors.regime}
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                {/* Financial Year */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="financial_year"
                    label="Financial Year (YYYY-YYYY)"
                    value={values.financial_year}
                    onChange={(e) =>
                      setFieldValue("financial_year", e.target.value)
                    }
                    fullWidth
                    error={
                      touched.financial_year && Boolean(errors.financial_year)
                    }
                    helperText={touched.financial_year && errors.financial_year}
                  />
                </Grid>

                {/* Tax Slab Table */}
                <Grid item xs={12}>
                  <Table
                    size="small"
                    sx={{
                      border: "1px solid rgba(224, 224, 224, 1)",
                      borderCollapse: "collapse",
                      "& th, & td": {
                        border: "1px solid rgba(224, 224, 224, 1)",
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell align="center" sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>Min Income</TableCell>
                        <TableCell align="center" sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>Max Income</TableCell>
                        <TableCell align="center" sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>Tax Rate (%)</TableCell>
                        <TableCell align="center" sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>Fixed Tax</TableCell>
                        <TableCell align="center" sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>Standard Deduction</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredSlabs.map((slab) => (
                        <TableRow key={slab.id}>
                          <TableCell align="center">{slab.min_income}</TableCell>
                          <TableCell align="center">{slab.max_income}</TableCell>
                          <TableCell align="center">{slab.tax_rate}</TableCell>
                          <TableCell align="center">{slab.fixed_tax}</TableCell>
                          <TableCell align="center">{slab.standard_deduction}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Grid>

                {/* ✅ Signature Pad */}
                <Grid item xs={12}>
  <Typography variant="subtitle1" gutterBottom>
    Authorized Signature
  </Typography>

  <Box display="flex" gap={2} alignItems="center">
    {/* Signature Pad */}
    <ReactSignatureCanvas
      ref={sigPadRef}
      penColor="black"
      canvasProps={{
        width: 500,
        height: 150,
        className: "sigCanvas",
        style: {
          border: "1px solid #ccc",
          borderRadius: "4px",
        },
      }}
    />

    {/* Show existing signature if editing */}
    {isEdit && initialData?.sign && (
      <Box>
        <Typography variant="body2" mb={1}>Existing Signature:</Typography>
        <img
          src={IMAGE_URL + initialData.sign}
          alt="Existing Signature"
          style={{
            border: "1px solid #ccc",
            borderRadius: 4,
            width: 200,
            height: 100,
            objectFit: "contain",
          }}
        />
      </Box>
    )}
  </Box>

  <Box mt={1}>
    <Button
      onClick={() => sigPadRef.current.clear()}
      variant="outlined"
      size="small"
    >
      Clear signature
    </Button>
  </Box>
</Grid>


                {/* Is Accepted */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="is_accepted"
                        checked={values.is_accepted}
                        onChange={(e) =>
                          setFieldValue("is_accepted", e.target.checked)
                        }
                      />
                    }
                    label="Is Accepted"
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button onClick={handleClose}>Cancel</Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
              >
                {isEdit ? "Update" : "Create"}
              </Button>
              {loading && <CircularProgress size={24} />}
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}

export default EmpTaxSlabModal;
